import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Plane, Coins, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TravelCreditsWidget — small profile-page card showing the member's
 * accumulated travel credit balance, how many months of membership
 * it's worth, and a Redeem button when the balance crosses the
 * monthly cost threshold.
 *
 * Auto-hides when both balance is 0 AND there are no pending clicks
 * so brand-new members don't see an empty placeholder.
 */
export default function TravelCreditsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${API}/travel-credits/me`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBalance(); }, []);

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      await axios.post(`${API}/travel-credits/me/redeem-month`);
      toast.success('Membership extended by 1 month');
      fetchBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not redeem');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return null;
  if (!data) return null;
  if (data.balance_cents === 0 && data.pending_clicks === 0) return null;

  const canRedeem = data.balance_cents >= 695;

  return (
    <Card className="border-amber-200 bg-amber-50/50" data-testid="travel-credits-widget">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Plane className="w-4 h-4 text-amber-600" />
          <h3 className="font-heading font-semibold text-sm">Travel Credits</h3>
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-amber-700 tabular-nums">
            ${data.balance_dollars.toFixed(2)}
          </span>
          {data.membership_months_equivalent > 0 && (
            <span className="text-xs text-muted-foreground">
              ≈ {data.membership_months_equivalent.toFixed(1)} month{data.membership_months_equivalent === 1 ? '' : 's'} of membership
            </span>
          )}
        </div>

        {data.pending_clicks > 0 && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {data.pending_clicks} booking{data.pending_clicks === 1 ? '' : 's'} pending confirmation
          </div>
        )}

        {canRedeem && (
          <Button
            size="sm"
            className="mt-3 w-full bg-amber-600 hover:bg-amber-700"
            onClick={handleRedeem}
            disabled={redeeming}
            data-testid="redeem-month-btn"
          >
            {redeeming ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Coins className="w-3.5 h-3.5 mr-1" />
            )}
            Redeem for 1 month free
          </Button>
        )}

        {data.recent_events.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Recent activity ({data.recent_events.length})
            </summary>
            <ul className="mt-2 space-y-1.5 text-xs">
              {data.recent_events.slice(0, 5).map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-2">
                  <span className="truncate text-muted-foreground">
                    {e.note || `${e.event_type} via ${e.source}`}
                  </span>
                  <span className={`font-mono tabular-nums shrink-0 ${e.amount_cents < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {e.amount_cents > 0 ? '+' : ''}${(e.amount_cents / 100).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}

        <p className="text-[11px] text-muted-foreground mt-3 italic">
          Earn credit by booking travel through Club partner links.
        </p>
      </CardContent>
    </Card>
  );
}
